/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { getAxios } from '@/lib/axios';
import { createQuery } from '@/utils/createReactQuery';
import { type QueryClient } from '@tanstack/react-query';
import { type AxiosInstance } from 'axios';
import { useLocale } from '@/hooks/useLocale';
import environmentConfig from '@/config/environment';

type AuthorRaw = {
  type: string;
  name: string;
  description?: string | null;
};

type ReviewRatingRaw = {
  type: string;
  ratingValue: number;
  bestRating: number;
  worstRating: number;
};

type ReviewRaw = {
  type: string;
  dateCreated?: string;
  datePublished?: string;
  reviewBody?: string;
  name?: string;
  reviewRating?: ReviewRatingRaw;
  author?: AuthorRaw;
};

type AggregateRatingRaw = {
  type: string;
  bestRating: number;
  ratingValue: number;
  reviewCount: number;
};

export type AuthenticDiscoveriesResponse = {
  reviews?: {
    context: string;
    review: ReviewRaw[];
    id: string;
  };
  ratings?: {
    context: string;
    aggregateRating: AggregateRatingRaw;
    id: string;
  };
  reviewSummary?: {
    context: string;
    review: ReviewRaw;
    id: string;
  };
};

export type AuthenticDiscoveriesOptions = {
  skuId: string;
  locale: string;
  canonical: string;
  maxReviewsCount?: number;
};

const getAuthenticDiscoveries = async (
  options: AuthenticDiscoveriesOptions,
  axiosInstance?: AxiosInstance,
): Promise<AuthenticDiscoveriesResponse> => {
  const { skuId, locale, canonical, maxReviewsCount = 100 } = options;

  const response = await getAxios(axiosInstance).get<AuthenticDiscoveriesResponse>(
    `${requestBaseURLGCPExtOrInt}/sls/product/product-reviews-integration-bs/v1/authentic-discoveries/${skuId}`,
    {
      params: {
        locale,
        canonical,
        maxReviewsCount,
      },
      withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
    },
  );

  return response.data;
};

const {
  useData: useAuthenticDiscoveriesData,
  prefetch: prefetchAuthenticDiscoveriesData,
  query: authenticDiscoveriesQuery,
} = createQuery('authenticDiscoveries', getAuthenticDiscoveries);

export { useAuthenticDiscoveriesData };

export const useAuthenticDiscoveries = (
  options: Omit<AuthenticDiscoveriesOptions, 'locale'> & {
    enabled?: boolean;
  },
) => {
  const { enabled = true, ...rest } = options;
  const locale = useLocale();

  return useAuthenticDiscoveriesData({
    ...rest,
    locale: locale.replace('-', '_'),
    enabled,
  });
};

export const prefetchAuthenticDiscoveries = (
  options: AuthenticDiscoveriesOptions,
  queryClient: QueryClient,
  axiosInstance: AxiosInstance,
) => {
  return prefetchAuthenticDiscoveriesData(queryClient, options, axiosInstance);
};

export const getAuthenticDiscoveriesFromCache = (
  options: AuthenticDiscoveriesOptions,
  queryClient: QueryClient,
) => {
  return queryClient.getQueryData<AuthenticDiscoveriesResponse>(
    authenticDiscoveriesQuery.getFullKey(options),
  );
};
